<template>
  <section class="rqa_jira_card" dir="rtl" :aria-label="'משימה ' + shortId">
    <header class="rqa_jira_head">
      <span class="rqa_small rqa_bold">משימה {{ shortId }}</span>
      <span v-if="jira" class="tag is-light rqa_jira_status">{{ statusLabel }}</span>
      <a
        v-if="jira && jira.web_url"
        class="rqa_small rqa_jira_link"
        :href="jira.web_url"
        target="_blank"
        rel="noopener noreferrer">{{ copy.openInJira }}</a>
    </header>

    <p v-if="!jira && entry.loading" class="rqa_muted rqa_small rqa_jira_loading" role="status">{{ copy.loading }}</p>
    <div v-else-if="!jira && entry.error" class="rqa_jira_failure">
      <span class="rqa_small">{{ copy.readFailure }}</span>
      <button type="button" class="button is-small rqa_jira_retry" @click="load(true)">{{ copy.retry }}</button>
    </div>

    <template v-if="jira">
      <p class="rqa_jira_title">{{ jira.title }}</p>
      <div v-if="draft" class="rqa_jira_draft">
        <p v-if="draft.title" class="rqa_bold rqa_jira_draft_title">{{ draft.title }}</p>
        <template v-if="draft.summary">
          <p class="rqa_small rqa_muted rqa_jira_label">{{ copy.proposedSolution }}</p>
          <p class="rqa_jira_summary" :class="{ rqa_jira_clamped: summaryLong && !expanded }">{{ draft.summary }}</p>
          <button v-if="summaryLong" type="button" class="button is-small is-white rqa_jira_more" :aria-expanded="expanded ? 'true' : 'false'" @click="expanded = !expanded">{{ expanded ? copy.showLess : copy.showMore }}</button>
        </template>
        <details v-for="list in lists" :key="list.key" class="rqa_jira_list" :class="'rqa_jira_' + list.key">
          <summary class="rqa_small">{{ list.label }} ({{ list.items.length }})</summary>
          <ul>
            <li v-for="(item, i) in list.items" :key="i" class="rqa_small">{{ itemText(item) }}</li>
          </ul>
        </details>
      </div>
      <p v-else class="rqa_muted rqa_small rqa_jira_no_draft">{{ copy.noDraft }}</p>
      <p v-if="entry.error" class="rqa_muted rqa_small">{{ copy.readFailure }} <a href="#" class="rqa_jira_retry" @click.prevent="load(true)">{{ copy.retry }}</a></p>
    </template>

    <!-- Decision feedback -->
    <div v-if="decision.state === 'pending'" class="rqa_jira_pending" role="status">
      <span class="rqa_small">{{ copy.pending }}</span>
      <button v-if="decision.pollExpired" type="button" class="button is-small rqa_jira_check" :class="{ 'is-loading': decision.checking }" :disabled="decision.checking" @click="checkAgain">{{ copy.checkAgain }}</button>
    </div>
    <div v-else-if="decision.state === 'unconfirmed'" class="rqa_jira_unconfirmed" role="alert">
      <span class="rqa_small">{{ copy.unconfirmed }}</span>
      <a v-if="jira && jira.web_url" class="rqa_small" :href="jira.web_url" target="_blank" rel="noopener noreferrer">{{ copy.openInJira }}</a>
      <button type="button" class="button is-small rqa_jira_check" :class="{ 'is-loading': decision.checking }" :disabled="decision.checking" @click="checkAgain">{{ copy.checkAgain }}</button>
    </div>
    <p v-else-if="decision.state === 'succeeded'" class="rqa_small rqa_jira_decided" role="status">{{ decidedText }}</p>
    <p v-else-if="decision.state === 'failed' && decision.message" class="rqa_error rqa_small rqa_jira_error" role="alert">{{ decision.message }}</p>

    <!-- Actions (server allowed_actions only, GET /config jira_writes) -->
    <div v-if="showActions && !panel" class="rqa_jira_actions">
      <button
        v-for="action in actions"
        :key="action"
        type="button"
        class="button is-small"
        :class="['rqa_jira_act_' + action, action === 'approve' ? 'is-primary is-light' : '']"
        :disabled="busy"
        @click="openPanel(action)">{{ labels[action] }}</button>
    </div>

    <div v-if="showActions && panel === 'approve'" class="rqa_jira_panel rqa_jira_approve_box" role="group" :aria-label="labels.approve">
      <p class="rqa_small">{{ copy.approveConfirm }}</p>
      <p v-if="astraPending" class="rqa_admin_warning rqa_small rqa_jira_override">{{ copy.astraOverride }}</p>
      <div class="rqa_jira_box_actions">
        <button type="button" class="button is-small is-primary rqa_jira_confirm" :class="{ 'is-loading': decision.state === 'submitting' }" :disabled="!canSubmit" @click="submit">{{ labels.approve }}</button>
        <button type="button" class="button is-small is-white rqa_jira_cancel" :disabled="busy" @click="closePanel">{{ copy.cancel }}</button>
      </div>
    </div>

    <div v-if="showActions && (panel === 'reject' || panel === 'edit')" class="rqa_jira_panel" :class="'rqa_jira_' + panel + '_box'">
      <label class="rqa_small rqa_bold" :for="inputId">{{ panel === 'edit' ? copy.editPrompt : copy.rejectPrompt }}</label>
      <textarea
        :id="inputId"
        ref="input"
        class="rqa_admin_input rqa_jira_input"
        rows="3"
        :maxlength="textMax"
        :value="text"
        :disabled="busy"
        @input="setText($event.target.value)"></textarea>
      <p v-if="panel === 'edit'" class="rqa_muted rqa_small rqa_jira_helper">{{ copy.editHelper }}</p>
      <span class="rqa_counter">{{ text.length }} / {{ textMax }}</span>
      <div class="rqa_jira_box_actions">
        <button type="button" class="button is-small is-primary rqa_jira_confirm" :class="{ 'is-loading': decision.state === 'submitting' }" :disabled="!canSubmit" @click="submit">{{ labels[panel] }}</button>
        <button type="button" class="button is-small is-white rqa_jira_cancel" :disabled="busy" @click="closePanel">{{ copy.cancel }}</button>
      </div>
    </div>
  </section>
</template>
<script>
import { jiraShortId } from '@/utils/recruiterQaFormat'
import { jiraStatusLabel, jiraAllowedActions, JIRA_ACTION_LABELS, JIRA_COPY } from '@/utils/recruiterQaJira'
import { JIRA_TEXT_MAX } from '@/services/recruiterQaApi'
import { jiraDraftKey, isJiraDecisionBusy } from '@/store/modules/recruiterQaAdmin'

const SUMMARY_COLLAPSE_CHARS = 280

// Jira task card under a supervisor-thread question turn. Loads on its own
// (never blocks the thread); plain-text rendering only. Decision buttons come
// from the server's allowed_actions and only when /config jira_writes is on.
export default {
  name: 'RecruiterQaJiraCard',
  props: {
    question: { type: Object, required: true },
    rootId: { type: Number, default: null }
  },
  data () {
    return { panel: null, expanded: false, copy: JIRA_COPY, labels: JIRA_ACTION_LABELS, textMax: JIRA_TEXT_MAX }
  },
  computed: {
    adminState () { return this.$store.state.recruiterQaAdmin },
    taskId () { return String(this.question.jira_task_id || '') },
    shortId () { return jiraShortId(this.taskId) },
    entry () { return this.adminState.jiraSummaries[this.taskId] || { loading: true, error: '', data: null } },
    jira () { return this.entry.data || null },
    draft () { return (this.jira && this.jira.draft) || null },
    statusLabel () { return jiraStatusLabel(this.jira) },
    astraPending () { return !!(this.jira && this.jira.approval && this.jira.approval.astra_pending) },
    summaryLong () { return String((this.draft && this.draft.summary) || '').length > SUMMARY_COLLAPSE_CHARS },
    lists () {
      const d = this.draft || {}
      return [
        { key: 'acceptance_criteria', label: JIRA_COPY.acceptanceCriteria, items: d.acceptance_criteria },
        { key: 'risks', label: JIRA_COPY.risks, items: d.risks },
        { key: 'open_questions', label: JIRA_COPY.openQuestions, items: d.open_questions }
      ].filter(l => Array.isArray(l.items) && l.items.length)
    },
    decision () { return this.adminState.jiraDecisions[this.question.id] || { state: 'idle' } },
    busy () { return isJiraDecisionBusy(this.decision) },
    canJiraWrite () { return this.$store.getters['recruiterQaAdmin/canJiraWrite'] === true },
    actions () { return jiraAllowedActions(this.jira) },
    showActions () { return this.canJiraWrite && !!this.jira && (this.actions.length > 0 || !!this.panel) },
    decidedText () { return JIRA_COPY.decided(this.decision.action) },
    draftKey () { return this.panel ? jiraDraftKey(this.rootId, this.panel, this.question.id) : '' },
    text () { return (this.draftKey && this.adminState.drafts[this.draftKey]) || '' },
    inputId () { return 'rqa-jira-input-' + this.question.id },
    canSubmit () {
      if (this.busy || !this.panel || this.actions.indexOf(this.panel) === -1) return false
      if (this.panel === 'approve') return true
      return this.text.trim().length > 0 && this.text.length <= JIRA_TEXT_MAX
    }
  },
  watch: {
    taskId () { this.load(false) }
  },
  mounted () {
    this.load(false)
    this.$store.dispatch('recruiterQaAdmin/resumeJiraDecision', { questionId: this.question.id })
  },
  methods: {
    load (force) {
      if (!this.taskId) return
      return this.$store.dispatch('recruiterQaAdmin/loadJiraSummary', { questionId: this.question.id, taskId: this.taskId, force: !!force })
    },
    itemText (item) {
      if (item === null || item === undefined) return ''
      if (typeof item === 'string') return item
      return item.text || item.title || item.description || JSON.stringify(item)
    },
    openPanel (action) {
      if (this.busy) return
      this.$store.dispatch('recruiterQaAdmin/clearJiraDecision', { questionId: this.question.id })
      this.panel = action
      if (action !== 'approve') this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus() })
    },
    closePanel () { this.panel = null },
    setText (value) {
      this.$store.dispatch('recruiterQaAdmin/setDraft', { key: this.draftKey, text: value })
    },
    async submit () {
      if (!this.canSubmit) return
      const res = await this.$store.dispatch('recruiterQaAdmin/submitJiraDecision', {
        questionId: this.question.id,
        action: this.panel,
        text: this.panel === 'approve' ? '' : this.text
      })
      if (res && (res.ok || res.pending)) this.closePanel()
      return res
    },
    checkAgain () {
      return this.$store.dispatch('recruiterQaAdmin/pollJiraDecision', { questionId: this.question.id, manual: true })
    }
  }
}
</script>
<style scoped>
.rqa_jira_card { border: 1px solid #dfe3ea; border-radius: 6px; padding: 8px 10px; margin-top: 8px; background: #fbfcfe; }
.rqa_jira_head { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 4px; }
.rqa_jira_link { margin-right: auto; }
.rqa_jira_title { font-weight: 600; margin: 0 0 4px; word-break: break-word; }
.rqa_jira_draft_title { margin: 0 0 4px; word-break: break-word; }
.rqa_jira_label { margin: 0; }
.rqa_jira_summary { white-space: pre-wrap; word-break: break-word; margin: 0 0 4px; font-size: 13px; }
.rqa_jira_clamped { max-height: 6.2em; overflow: hidden; }
.rqa_jira_list summary { cursor: pointer; }
.rqa_jira_list ul { margin: 2px 18px 4px 0; list-style: disc; }
.rqa_jira_failure, .rqa_jira_pending, .rqa_jira_unconfirmed { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.rqa_jira_decided { color: #1f6f43; margin: 6px 0 0; }
.rqa_jira_error { margin: 6px 0 0; }
.rqa_jira_unconfirmed { color: #7a5b00; }
.rqa_jira_actions, .rqa_jira_box_actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.rqa_jira_panel { border-top: 1px solid #eee; margin-top: 6px; padding-top: 6px; }
.rqa_jira_helper { margin: 4px 0 0; }
.rqa_admin_input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  padding: 8px 10px;
  direction: rtl;
  resize: vertical;
}
.rqa_admin_warning { color: #7a5b00; background: #fff3d6; border-radius: 4px; padding: 4px 6px; margin: 4px 0 0; }
.rqa_counter { font-size: 11px; color: #888; }
.rqa_bold { font-weight: 600; }
.rqa_muted { color: #777; }
.rqa_small { font-size: 12px; }
.rqa_error { color: #a12620; }
</style>
